import React, { useState, useEffect, useCallback } from 'react';
import { AdventureInvite, AdventureInviteListRow, UserId } from 'coop-adventure-engine';
import adventureInvitesService from '../../services/adventureInvitesService';
import { AdventureInviteCard } from './AdventureInviteCard';
import apiService from '../../services/api';

export interface AdventureInviteListProps {
  /**
   * Server-hydrated invite rows from scene payload (preferred).
   * When set, the list does not fetch invites or session variables separately.
   */
  rows?: AdventureInviteListRow[];

  /**
   * Optional click handler for adventure invites
   * If not provided, invites will use default join behavior (joinAdventureFromInvite)
   */
  onInviteClick?: (inviteId: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom styles
   */
  style?: React.CSSProperties;

  /**
   * Optional custom button color for invite cards (e.g. from adventure button_color)
   */
  customColor?: string;
}

/**
 * Component for displaying list of adventure invites
 * All invites are clickable and will join the adventure when clicked
 */
const getColorWithAlpha = (color: string, alpha: string) => {
  if (color.startsWith('#')) {
    return `${color}${alpha}`;
  }
  return color;
};

function rowToInvite(row: AdventureInviteListRow): AdventureInvite {
  return {
    id: row.inviteId,
    sessionId: row.sessionId,
    adventureId: row.adventureId,
    playerSlot: row.playerSlot,
    inviterId: row.inviterId,
    inviteeId: row.inviteeId,
    status: 'pending',
    createdAt: new Date(0)
  };
}

export const AdventureInviteList: React.FC<AdventureInviteListProps> = ({
  rows,
  onInviteClick,
  className = '',
  style,
  customColor
}) => {
  const useHydratedRows = rows !== undefined;
  const [invites, setInvites] = useState<AdventureInvite[]>([]);
  const [isLoading, setIsLoading] = useState(!useHydratedRows);
  const [error, setError] = useState<string | null>(null);
  const [userInfoCache, setUserInfoCache] = useState<Map<UserId, { name: string; portraitId?: string }>>(new Map());

  const loadAdventureInvites = useCallback(async () => {
    if (useHydratedRows) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const adventureInvites = await adventureInvitesService.getAdventureInvites();
      setInvites(adventureInvites);

      setUserInfoCache((prevCache) => {
        const newCache = new Map(prevCache);
        const invitesToLoad = adventureInvites.filter(inv => !newCache.has(inv.inviterId));

        Promise.all(
          invitesToLoad.map(async (invite) => {
            try {
              const userInfo = await apiService.getUserInfo(invite.inviterId);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                const cacheEntry: { name: string; portraitId?: string } = {
                  name: userInfo.playerName || userInfo.username || `User ${invite.inviterId.substring(0, 8)}`
                };
                if (userInfo.portraitId) {
                  cacheEntry.portraitId = userInfo.portraitId;
                }
                updatedCache.set(invite.inviterId, cacheEntry);
                return updatedCache;
              });
            } catch (err) {
              console.error(`Failed to load user info for ${invite.inviterId}:`, err);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                updatedCache.set(invite.inviterId, {
                  name: `User ${invite.inviterId.substring(0, 8)}`
                });
                return updatedCache;
              });
            }
          })
        );

        return newCache;
      });
    } catch (err) {
      console.error('Failed to load adventure invites:', err);
      setError(err instanceof Error ? err.message : 'Failed to load adventure invites');
    } finally {
      setIsLoading(false);
    }
  }, [useHydratedRows]);

  useEffect(() => {
    if (!useHydratedRows || !rows?.length) {
      return;
    }

    const inviteeIds = rows.map(row => row.inviterId);

    void Promise.all(
      inviteeIds.map(async (inviterId) => {
        try {
          const userInfo = await apiService.getUserInfo(inviterId);
          setUserInfoCache((currentCache) => {
            if (currentCache.has(inviterId)) {
              return currentCache;
            }
            const updatedCache = new Map(currentCache);
            updatedCache.set(inviterId, {
              name: userInfo.playerName || userInfo.username || `User ${inviterId.substring(0, 8)}`,
              ...(userInfo.portraitId ? { portraitId: userInfo.portraitId } : {})
            });
            return updatedCache;
          });
        } catch (err) {
          console.error(`Failed to load user info for ${inviterId}:`, err);
        }
      })
    );
  }, [rows, useHydratedRows]);

  useEffect(() => {
    if (useHydratedRows) {
      return;
    }

    loadAdventureInvites();

    const handleAdventureInviteUpdate = () => {
      loadAdventureInvites();
    };

    window.addEventListener('adventure_invite:update', handleAdventureInviteUpdate);
    return () => {
      window.removeEventListener('adventure_invite:update', handleAdventureInviteUpdate);
    };
  }, [loadAdventureInvites, useHydratedRows]);

  const handleInviteClick = useCallback(
    (inviteId: string) => {
      if (onInviteClick) {
        onInviteClick(inviteId);
      } else {
        adventureInvitesService.joinAdventureFromInvite(inviteId)
          .then(({ sessionId, playerId }) => {
            window.location.href = `/scene/${sessionId}/${playerId}`;
          })
          .catch((joinError) => {
            console.error('Failed to join adventure from invite:', joinError);
          });
      }
    },
    [onInviteClick]
  );

  const displayRows = useHydratedRows ? rows : undefined;
  const displayInvites = useHydratedRows
    ? (rows ?? []).map(rowToInvite)
    : invites;

  if (!useHydratedRows && isLoading) {
    return (
      <div
        className={`adventure-invite-list loading ${className}`}
        style={style}
        data-testid="adventure-invite-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic text-sm">Loading adventure invites...</p>
        </div>
      </div>
    );
  }

  if (!useHydratedRows && error) {
    return (
      <div
        className={`adventure-invite-list error ${className}`}
        style={style}
        data-testid="adventure-invite-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (displayInvites.length === 0) {
    return (
      <div
        className={`adventure-invite-list empty ${className}`}
        style={style}
        data-testid="adventure-invite-list-empty"
      >
        <div className="flex items-center justify-center py-2" data-testid="empty-adventure-invites">
          <p className="italic text-center text-gray-400">No adventure invites</p>
        </div>
      </div>
    );
  }

  const useCustomContentLayout = Boolean(
    displayRows?.some(row => row.cardContent && row.cardContent.trim())
  );

  return (
    <div
      className={`adventure-invite-list ${className}`}
      style={style}
      data-testid="adventure-invite-list"
    >
      <div
        className={`adventure-invite-list-items ${
          useCustomContentLayout ? 'flex flex-col w-full' : 'flex flex-wrap justify-center'
        }`}
        style={useCustomContentLayout ? { gap: '0.75rem' } : { gap: '0.5rem' }}
        data-testid="adventure-invite-items-list"
      >
        {displayInvites.map((invite, index) => {
          const userInfo = userInfoCache.get(invite.inviterId);
          const displayName = userInfo?.name || `User ${invite.inviterId.substring(0, 8)}`;
          const hydratedRow = displayRows?.[index];
          const hasCustomHeader = Boolean(hydratedRow?.cardContent?.trim());
          const buttonStyle: React.CSSProperties = hasCustomHeader
            ? {
                width: '100%',
                maxWidth: '100%',
                minHeight: 48
              }
            : {};
          if (customColor) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }

          return (
            <button
              key={invite.id}
              onClick={() => handleInviteClick(invite.id)}
              className={`adventure-invite-item-button inventory-item-button${
                hasCustomHeader ? ' adventure-invite-item-button--custom-content w-full' : ''
              }`}
              style={buttonStyle}
              data-testid={`adventure-invite-${invite.id}`}
              data-invite-id={invite.id}
              aria-label={`Join adventure invite from ${displayName}`}
            >
              <AdventureInviteCard
                invite={invite}
                inviteId={invite.id}
                noFrame={true}
                className="adventure-invite-item-adventure-card"
                {...(hydratedRow?.cardContent ? { cardContent: hydratedRow.cardContent } : {})}
                {...(customColor ? { customColor } : {})}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdventureInviteList;
